import { LiveKitRoom } from '@livekit/components-react';
import useAppStore from '../../stores/app';
import config from '../../config';

/**
 * VtcRoomConnection - Hidden LiveKit room connection for VTC proctor room
 * 
 * This component establishes and maintains the LiveKit connection for the VTC's
 * proctor room (for proctor-to-proctor communication) WITHOUT wrapping any UI
 * in a LiveKitRoom context. This prevents React Context nesting issues that
 * cause cross-talk between SessionViewer rooms.
 * 
 * **Important:**
 * - Renders NO UI (returns null)
 * - Only manages the LiveKit connection lifecycle
 * - Does not create a React Context that child components can access
 * - SessionViewers have their own isolated LiveKitRoom contexts
 * 
 * @component
 * @param {Object} props
 * @param {Function} props.onConnectionChange - Callback when connection state changes (connected: boolean)
 * @returns {null} No UI rendered
 * 
 * @see PR-3412 for cross-talk investigation and resolution
 */
const VtcRoomConnection = ({ onConnectionChange }) => {
    const mediaRoom = useAppStore((state) => state.mediaRoom);
    const mediaClient = useAppStore((state) => state.mediaClient);

    // Don't render anything if no room/client configured
    if (!mediaRoom || !mediaClient) {
        return null;
    }

    return (
        <LiveKitRoom
            room={mediaRoom}
            token={mediaClient?.mediaToken}
            serverUrl={config.media?.livekit?.websocketUrl}
            connect={true}
            onConnected={() => {
                console.log('[VtcRoomConnection] Connected to VTC proctor room');
                if (onConnectionChange) {
                    onConnectionChange(true);
                }
            }}
            onDisconnected={(reason) => {
                console.log('[VtcRoomConnection] Disconnected from VTC proctor room:', reason);
                if (onConnectionChange) {
                    onConnectionChange(false);
                }
            }}
        >
            {/* Render nothing - this component only manages the connection */}
        </LiveKitRoom>
    );
};

export default VtcRoomConnection;
